import { useState, useCallback, useEffect } from 'react';
import { useCalculoPagamento } from './useCalculoPagamento';
import api from '../../../services/api';

/**
 * Hook personalizado para gerenciar o estado e as operações relacionadas a parcelas de pagamento
 */
export const useParcelas = (allAvailableParcelas = []) => {
  // NOVO LOG: Log da entrada do hook useParcelas
  console.log('useParcelas: Hook iniciado com allAvailableParcelas:', allAvailableParcelas);

  // Estados do hook
  const [selectedParcelas, setSelectedParcelas] = useState([]); // Inicia vazio
  const [valoresPagamento, setValoresPagamento] = useState({});
  const [valoresCalculados, setValoresCalculados] = useState({}); // Mudará para armazenar objeto detalhado
  const [emProcessamento, setEmProcessamento] = useState(false);

  // Inicializa o hook de cálculo de pagamento
  const { processarParcela } = useCalculoPagamento();

  // Inicializa os valores das parcelas quando o array de parcelas iniciais muda
  useEffect(() => {
    // NOVO LOG: Log da execução do useEffect em useParcelas
    console.log('useParcelas useEffect: allAvailableParcelas mudou para:', allAvailableParcelas);

    if (Array.isArray(allAvailableParcelas) && allAvailableParcelas.length > 0) {
      console.log('useParcelas useEffect: Inicializando valores e cálculos com:', allAvailableParcelas);

      const valoresIniciais = {};
      const valoresCalculadosIniciais = {}; // Será um objeto com detalhes

      allAvailableParcelas.forEach(parcela => {
        const valorOriginal = parseFloat(parcela.valor) || 0;
        valoresIniciais[parcela.id] = valorOriginal; // Valor inicial para o campo de input
        valoresCalculadosIniciais[parcela.id] = { // Objeto detalhado para cada parcela
          valorFinal: valorOriginal,
          jurosAutomaticos: 0,
          jurosManuais: 0,
          desconto: 0,
          jurosAutomaticosPercentual: 0,
          jurosManuaisPercentual: 0,
          descontoPercentual: 0,
        };
      });

      setValoresPagamento(valoresIniciais);
      setValoresCalculados(valoresCalculadosIniciais);
      setSelectedParcelas([]); // Limpa seleção
      console.log('useParcelas useEffect: seleção e valores resetados/inicializados.');
    } else { // if (Array.isArray(allAvailableParcelas) && allAvailableParcelas.length === 0)
      // Se não há parcelas disponíveis, ou a lista foi limpa, limpar todos os estados
      setSelectedParcelas([]);
      setValoresPagamento({});
      setValoresCalculados({});
      console.log('useParcelas useEffect: Nenhuma parcela disponível ou lista vazia, todos os estados limpos.');
    }
  }, [allAvailableParcelas]); // Dependência em allAvailableParcelas para reagir a mudanças

  /**
   * Função para lidar com a seleção/desseleção de uma parcela
   */
  const toggleParcelaSelection = useCallback((parcela) => {
    setSelectedParcelas(prevSelected => {
      const isSelected = prevSelected.some(p => p.id === parcela.id);
      if (isSelected) {
        return prevSelected.filter(p => p.id !== parcela.id);
      } else {
        return [...prevSelected, parcela];
      }
    });
  }, []);

  /**
   * Seleciona todas as parcelas disponíveis
   */
  const selectAllParcelas = useCallback(() => {
    setSelectedParcelas(allAvailableParcelas);
  }, [allAvailableParcelas]);

  /**
   * Remove a seleção de todas as parcelas
   */
  const deselectAllParcelas = useCallback(() => {
    setSelectedParcelas([]);
  }, []);

  /**
   * Atualiza o valor de pagamento de uma parcela específica
   */
  const processarPagamentoParcela = useCallback((parcela, valor) => {
    setValoresPagamento(prev => ({
      ...prev,
      [parcela.id]: parseFloat(valor) || 0
    }));
  }, []);

  /**
   * Atualiza o valor de uma parcela específica
   */
  const atualizarValorParcela = useCallback((parcelaId, novoValor) => {
    setValoresPagamento(prev => ({
      ...prev,
      [parcelaId]: parseFloat(novoValor) || 0
    }));
  }, []);

  /**
   * Processa todas as parcelas com as regras de juros e descontos fornecidas
   */
  const processarTodasAsParcelas = useCallback(async ({
    parcelasParaProcessar,
    tipoJuros,
    jurosPercentual,
    valorJurosFixo,
    tipoDesconto,
    descontoPercentual,
    valorDescontoFixo,
    aplicarJurosAutomaticos
  }) => {
    // VALIDAÇÃO: Garantir que parcelasParaProcessar seja um array válido
    if (!parcelasParaProcessar || !Array.isArray(parcelasParaProcessar)) {
      console.error('useParcelas processarTodasAsParcelas: parcelasParaProcessar não é um array válido:', parcelasParaProcessar);
      return { novosValores: {}, totais: { juros: 0, jurosManuais: 0, jurosAutomaticos: 0, descontos: 0, calculado: 0, original: 0 } };
    }

    if (parcelasParaProcessar.length === 0) {
      console.warn('useParcelas processarTodasAsParcelas: Nenhuma parcela para processar');
      return { novosValores: {}, totais: { juros: 0, jurosManuais: 0, jurosAutomaticos: 0, descontos: 0, calculado: 0, original: 0 } };
    }

    console.log('useParcelas processarTodasAsParcelas: Parâmetros recebidos:', {
      parcelasParaProcessar: parcelasParaProcessar.length, tipoJuros, jurosPercentual, valorJurosFixo,
      tipoDesconto, descontoPercentual, valorDescontoFixo, aplicarJurosAutomaticos
    });
    const novosValores = {}; // Armazenará objetos detalhados
    const totais = {
      juros: 0,
      jurosManuais: 0,
      jurosAutomaticos: 0,
      descontos: 0,
      calculado: 0,
      original: 0
    };

    for (const parcela of parcelasParaProcessar) {
      try {
        console.group(`Processando parcela ${parcela.id || 'sem ID'}`);

        // Se juros automáticos estiverem ativos, buscar do backend
        let jurosAutomaticos = 0;
        let jurosAutomaticosPercentual = 0;
        let diasAtraso = undefined;
        let periodicidade = undefined;
        let taxa = undefined;
        if (aplicarJurosAutomaticos) {
          try {
            const response = await api.get(`/parcelas/${parcela.id}/juros-automaticos`);
            if (response.status === 200) {
              const data = response.data;
              jurosAutomaticos = data.valor;
              jurosAutomaticosPercentual = data.percentual;
              diasAtraso = data.diasAtraso;
              periodicidade = data.periodicidade;
              taxa = data.taxa;
              console.log(`Juros automáticos do backend para parcela ${parcela.id}:`, data);
            }
          } catch (error) {
            console.error(`Erro ao buscar juros automáticos do backend para parcela ${parcela.id}:`, error);
          }
        }

        const resultado = await processarParcela(
          parcela,
          [], // Não usar regras de juros locais
          valoresPagamento,
          valoresCalculados,
          tipoJuros,
          jurosPercentual,
          valorJurosFixo,
          tipoDesconto,
          descontoPercentual,
          valorDescontoFixo,
          aplicarJurosAutomaticos
        );

        console.log(`[useParcelas][processarTodasAsParcelas] Resultado de processarParcela para ${parcela.id}:`, resultado);

        // Armazena os detalhes do cálculo para uso posterior
        if (resultado.detalhes) {
          // Atualiza totais
          totais.original += resultado.detalhes.valorOriginal || 0;
          totais.jurosManuais += resultado.detalhes.jurosManuais || 0;
          totais.jurosAutomaticos += jurosAutomaticos; // Usar juros do backend
          totais.descontos += resultado.detalhes.desconto || 0;
          totais.calculado += resultado.valorFinal + jurosAutomaticos; // Valor final já inclui juros manuais, somamos apenas juros automáticos
          totais.juros = totais.jurosManuais + totais.jurosAutomaticos;

          // Armazena o objeto detalhado para esta parcela
          novosValores[resultado.id] = {
            valorFinal: resultado.valorFinal + jurosAutomaticos, // Adicionar juros do backend
            jurosAutomaticos: jurosAutomaticos,
            jurosManuais: resultado.detalhes.jurosManuais || 0,
            desconto: resultado.detalhes.desconto || 0,
            jurosAutomaticosPercentual: jurosAutomaticosPercentual,
            jurosManuaisPercentual: resultado.detalhes.jurosManuaisPercentual || 0,
            descontoPercentual: resultado.detalhes.descontoPercentual || 0,
            diasAtraso: diasAtraso,
            periodicidade: periodicidade,
            taxa: taxa
          };
        }

        console.groupEnd();
      } catch (error) {
        console.error(`Erro ao processar parcela ${parcela.id || 'sem ID'}:`, error);
      }
    }

    // Após processar todas as parcelas, atualizamos os estados
    setValoresCalculados(novosValores);

    // Atualizar valoresPagamento
    const newValuesForPayment = {};
    Object.entries(novosValores).forEach(([id, detalheCalculo]) => {
      newValuesForPayment[id] = detalheCalculo.valorFinal !== undefined ? detalheCalculo.valorFinal.toFixed(2) : '0.00';
    });
    setValoresPagamento(prev => ({ ...prev, ...newValuesForPayment }));

    return { novosValores, totais };
  }, [processarParcela, selectedParcelas, valoresPagamento, valoresCalculados, setValoresCalculados, setValoresPagamento]);

  /**
   * Recalcula todos os valores com base nos juros e descontos fornecidos
   */
  const recalcularTodosValores = useCallback(async ({
    parcelasParaProcessar: parcelasRecebidas, // Renomear para evitar conflito
    tipoJuros,
    jurosPercentual,
    valorJurosFixo,
    tipoDesconto,
    descontoPercentual,
    valorDescontoFixo,
    aplicarJurosAutomaticos,
    forceRecalculate = false
  }) => {
    console.log('useParcelas recalcularTodosValores: Iniciando recálculo.', { forceRecalculate, parcelasRecebidas });
    if (emProcessamento && !forceRecalculate) {
      console.log('useParcelas recalcularTodosValores: Já em processamento, ignorando recálculo.');
      return;
    }
    setEmProcessamento(true);

    try {
      // VALIDAÇÃO: Garantir que parcelasParaProcessar seja sempre um array válido
      let parcelasParaProcessar = [];

      if (parcelasRecebidas && Array.isArray(parcelasRecebidas) && parcelasRecebidas.length > 0) {
        // Se parcelas foram passadas explicitamente, usar elas
        parcelasParaProcessar = parcelasRecebidas;
        console.log('useParcelas recalcularTodosValores: Usando parcelas recebidas:', parcelasParaProcessar.length);
      } else if (selectedParcelas && Array.isArray(selectedParcelas) && selectedParcelas.length > 0) {
        // Se há parcelas selecionadas, usar elas
        parcelasParaProcessar = selectedParcelas;
        console.log('useParcelas recalcularTodosValores: Usando parcelas selecionadas:', parcelasParaProcessar.length);
      } else if (allAvailableParcelas && Array.isArray(allAvailableParcelas) && allAvailableParcelas.length > 0) {
        // Se não há seleção, usar todas as disponíveis
        parcelasParaProcessar = allAvailableParcelas;
        console.log('useParcelas recalcularTodosValores: Usando todas as parcelas disponíveis:', parcelasParaProcessar.length);
      } else {
        // Se não há parcelas válidas, retornar sem fazer nada
        console.warn('useParcelas recalcularTodosValores: Nenhuma parcela válida encontrada para processar');
        setEmProcessamento(false);
        return null;
      }

      console.log('useParcelas recalcularTodosValores: Parcelas para processar:', parcelasParaProcessar);

      const { novosValores, totais } = await processarTodasAsParcelas({
        parcelasParaProcessar,
        tipoJuros,
        jurosPercentual,
        valorJurosFixo,
        tipoDesconto,
        descontoPercentual,
        valorDescontoFixo,
        aplicarJurosAutomaticos
      });

      setValoresCalculados(novosValores);
      return totais;
    } catch (error) {
      console.error('useParcelas recalcularTodosValores: Erro ao recalcular valores:', error);
      return null; // Retorna nulo em caso de erro
    } finally {
      setEmProcessamento(false);
      console.log('useParcelas recalcularTodosValores: Recálculo finalizado. EmProcessamento:', false);
    }
  }, [selectedParcelas, allAvailableParcelas, emProcessamento, processarTodasAsParcelas, setValoresCalculados, setValoresPagamento]);

  return {
    selectedParcelas,
    valoresCalculados,
    emProcessamento,
    valoresPagamento,
    setValoresPagamento,
    setValoresCalculados,
    toggleParcelaSelection,
    selectAllParcelas,
    deselectAllParcelas,
    atualizarValorParcela,
    processarPagamentoParcela,
    recalcularValores: processarTodasAsParcelas, // Renomeado para uso externo
    // Recalcular todos os valores do modal
    recalcularTodosValores: processarTodasAsParcelas
  };
};
